/** A válasz alakja nem felel meg a szerződésnek (a felület külön üzenettel jelzi). */
export class ElemzesAlakHiba extends Error {}

/** Az `elemzes` adatbázis-függvény válaszának alakja (terv: „Elemzés: adatszerződés”) és futásidejű ellenőrzése. */
export interface ElemzesMutatok {
  inditasok: number;
  megerositett: number;
  megerositesi_arany: number | null;
  reakcio_median_perc: number | null;
  hataridon_belul_arany: number | null;
  szemle_arany: number | null;
  megbizas_arany: number | null;
}
export interface TolcserLepesAdat { lepes: string; db: number }
export interface NapiSor { nap: string; megerositett: number; nem_megerositett: number }
export interface ElveszettOkSor { ok: string; db: number }
export interface ForrasSor { forras: string | null; kampany: string | null; db: number; szemle: number; megbizas: number }
export interface KollegaSor { user_id: string; nev: string; db: number; elerve: number; reakcio_median_perc: number | null; szemle: number; megbizas: number }
export interface IrodaSor {
  office_id: string; nev: string; db: number; reakcio_median_perc: number | null; hataridon_belul_arany: number | null;
  szemle_arany: number | null; megbizas_arany: number | null; csak_erdeklodo_arany: number | null; sms_db: number;
}
export interface ErtekDb { ertek: string; db: number }
export interface Ingatlanok {
  tipusok: { tipus: string; db: number }[];
  teruletek: { nev: string; db: number }[];
  arelvaras: ErtekDb[];
  idosav: ErtekDb[];
  szakertoi_arany: number | null;
}
export interface ElemzesValasz {
  idoszak: { tol: string; ig: string };
  mutatok: ElemzesMutatok;
  elozo_mutatok: ElemzesMutatok;
  tolcser: TolcserLepesAdat[];
  napi: NapiSor[];
  elveszett_okok: ElveszettOkSor[];
  /** Az időszak megerősített leadjei közül a „csak érdeklődött” okkal elveszettek. */
  csak_erdeklodo: { db: number; arany: number | null };
  /** A most „később aktuális” leadek (az időszaktól függetlenül); esedékes: az újrahívás hónapja a folyó vagy korábbi. */
  kesobb_aktualis: { db: number; esedekes_db: number; honapok: { honap: string; db: number }[] };
  forrasok: ForrasSor[];
  kollegak: KollegaSor[];
  irodak: IrodaSor[];
  ingatlanok: Ingatlanok;
  sms: { verify_db: number; ertesito_db: number };
}

/** Egy eladott ingatlan az `elemzes_eladasok` válaszában. A becslés szakértői (szám nélküli) becslésnél null. */
export interface EladasSor {
  lead_id: string; eladva_at: string; office_id: string; iroda: string; tipus: string; iranyitoszam: string;
  terulet: string | null; utca: string | null; megbizas_tipus: string | null; tenyleges_ar: number;
  becsles_min: number | null; becsles_kozep: number | null; becsles_max: number | null; kollega: string | null;
}
/** Az `elemzes_eladasok` adatbázis-függvény válasza: megbízások és eladások a rögzítés napja szerint. */
export interface EladasokValasz {
  megbizasok: { kizarolagos: number; nem_kizarolagos: number };
  db: number;
  osszeg: number;
  becsult_db: number;
  savon_belul_db: number;
  elteres_atlag: number | null;
  lista: EladasSor[];
}

type Kind = 'szam' | 'szam?' | 'szoveg' | 'szoveg?';
type Alak = { [kulcs: string]: Kind | Alak | [Alak] };

const MUTATOK: Alak = {
  inditasok: 'szam', megerositett: 'szam', megerositesi_arany: 'szam?', reakcio_median_perc: 'szam?',
  hataridon_belul_arany: 'szam?', szemle_arany: 'szam?', megbizas_arany: 'szam?',
};
const ALAK: Alak = {
  idoszak: { tol: 'szoveg', ig: 'szoveg' },
  mutatok: MUTATOK,
  elozo_mutatok: MUTATOK,
  tolcser: [{ lepes: 'szoveg', db: 'szam' }],
  napi: [{ nap: 'szoveg', megerositett: 'szam', nem_megerositett: 'szam' }],
  elveszett_okok: [{ ok: 'szoveg', db: 'szam' }],
  csak_erdeklodo: { db: 'szam', arany: 'szam?' },
  kesobb_aktualis: { db: 'szam', esedekes_db: 'szam', honapok: [{ honap: 'szoveg', db: 'szam' }] },
  forrasok: [{ forras: 'szoveg?', kampany: 'szoveg?', db: 'szam', szemle: 'szam', megbizas: 'szam' }],
  kollegak: [{ user_id: 'szoveg', nev: 'szoveg', db: 'szam', elerve: 'szam', reakcio_median_perc: 'szam?', szemle: 'szam', megbizas: 'szam' }],
  irodak: [{
    office_id: 'szoveg', nev: 'szoveg', db: 'szam', reakcio_median_perc: 'szam?', hataridon_belul_arany: 'szam?',
    szemle_arany: 'szam?', megbizas_arany: 'szam?', csak_erdeklodo_arany: 'szam?', sms_db: 'szam',
  }],
  ingatlanok: {
    tipusok: [{ tipus: 'szoveg', db: 'szam' }],
    teruletek: [{ nev: 'szoveg', db: 'szam' }],
    arelvaras: [{ ertek: 'szoveg', db: 'szam' }],
    idosav: [{ ertek: 'szoveg', db: 'szam' }],
    szakertoi_arany: 'szam?',
  },
  sms: { verify_db: 'szam', ertesito_db: 'szam' },
};
const ELADASOK_ALAK: Alak = {
  megbizasok: { kizarolagos: 'szam', nem_kizarolagos: 'szam' },
  db: 'szam', osszeg: 'szam', becsult_db: 'szam', savon_belul_db: 'szam', elteres_atlag: 'szam?',
  lista: [{
    lead_id: 'szoveg', eladva_at: 'szoveg', office_id: 'szoveg', iroda: 'szoveg', tipus: 'szoveg', iranyitoszam: 'szoveg',
    terulet: 'szoveg?', utca: 'szoveg?', megbizas_tipus: 'szoveg?', tenyleges_ar: 'szam',
    becsles_min: 'szam?', becsles_kozep: 'szam?', becsles_max: 'szam?', kollega: 'szoveg?',
  }],
};

/** Az `elemzes_korzet` adatbázis-függvény válasza: a körzeten kívül elutasított kérések (névtelen napi számláló). */
export interface KorzetValasz {
  db: number;
  teruletek: { kod: string; nev: string; darab: number }[];
  inditott: number;
  arany: number | null;
}

function leiro(x: unknown): string {
  return x === null ? 'null' : Array.isArray(x) ? 'tömb' : typeof x;
}

const KORZET_ALAK: Alak = {
  db: 'szam', teruletek: [{ kod: 'szoveg', nev: 'szoveg', darab: 'szam' }], inditott: 'szam', arany: 'szam?',
};

function ellenorzes(x: unknown, alak: Kind | Alak | [Alak], ut: string): void {
  if (typeof alak === 'string') {
    const nullable = alak.endsWith('?');
    if (x === null && nullable) return;
    const tipus = alak.startsWith('szam') ? 'number' : 'string';
    if (typeof x === tipus && (tipus !== 'number' || Number.isFinite(x))) return;
    throw new ElemzesAlakHiba(`Az elemzés válasza nem a várt alakú: a(z) ${ut} ${tipus === 'number' ? 'szám' : 'szöveg'}${nullable ? ' vagy null' : ''} lenne, de ${leiro(x)}.`);
  }
  if (Array.isArray(alak)) {
    if (!Array.isArray(x)) throw new ElemzesAlakHiba(`Az elemzés válasza nem a várt alakú: a(z) ${ut} tömb lenne, de ${leiro(x)}.`);
    x.forEach((elem, i) => ellenorzes(elem, alak[0], `${ut}[${i}]`));
    return;
  }
  if (typeof x !== 'object' || x === null || Array.isArray(x)) {
    throw new ElemzesAlakHiba(`Az elemzés válasza nem a várt alakú: a(z) ${ut || 'válasz'} objektum lenne, de ${leiro(x)}.`);
  }
  for (const [kulcs, al] of Object.entries(alak)) {
    const kulcsUt = ut ? `${ut}.${kulcs}` : kulcs;
    if (!(kulcs in x)) throw new ElemzesAlakHiba(`Az elemzés válasza nem a várt alakú: hiányzik a(z) ${kulcsUt}.`);
    ellenorzes((x as Record<string, unknown>)[kulcs], al, kulcsUt);
  }
}

function alakEllenorzes<T>(valasz: unknown, alak: Alak): T {
  if (typeof valasz !== 'object' || valasz === null || Array.isArray(valasz)) {
    throw new ElemzesAlakHiba(`Az elemzés válasza nem objektum (${leiro(valasz)}). Frissítsd az oldalt; ha ismétlődik, szólj nekünk.`);
  }
  ellenorzes(valasz, alak, '');
  return valasz as T;
}

/** Ellenőrzi, hogy a `Json` válasz megfelel-e a szerződésnek; különben érthető hibát dob. */
export function ellenorizElemzes(valasz: unknown): ElemzesValasz {
  return alakEllenorzes<ElemzesValasz>(valasz, ALAK);
}

/** Ugyanez az `elemzes_eladasok` válaszára. */
export function ellenorizEladasok(valasz: unknown): EladasokValasz {
  return alakEllenorzes<EladasokValasz>(valasz, ELADASOK_ALAK);
}

/** Ugyanez az `elemzes_korzet` válaszára. */
export function ellenorizKorzet(valasz: unknown): KorzetValasz {
  return alakEllenorzes<KorzetValasz>(valasz, KORZET_ALAK);
}
